import { useCallback } from 'react';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';

import { useContextualizedBonusDetails } from '../../../hooks/use-contextualized-bonus-details';
import { getCelebrationType } from '../../../utils';
import { useReactionContext } from '../reaction-select-context';

enum Action {
	Create = 'Create',
	Remove = 'Delete',
	Replace = 'Replace',
}

// Map internal Action enum to AnalyticsEvent
const eventMap = {
	[Action.Create]: AnalyticsEvent.Recognition.ReactionCreateAction,
	[Action.Remove]: AnalyticsEvent.Recognition.ReactionDeleteAction,
	[Action.Replace]: AnalyticsEvent.Recognition.ReactionReplaceAction,
} as const;

export const useTrackReaction = () => {
	const { bonus } = useReactionContext();
	const { isCreatedByUser, isUserReceiver } = useContextualizedBonusDetails(bonus);
	const { track } = useAnalytics();

	const sendEvent = useCallback(
		(action: Action, type: string) => {
			track(eventMap[action], {
				celebrationType: getCelebrationType(bonus),
				isUserGiver: isCreatedByUser,
				isUserReceiver,
				reactionType: type,
			});
		},
		[bonus, isCreatedByUser, isUserReceiver, track]
	);

	const createTracker = useCallback(
		(action: Action) => {
			return (type: string) => sendEvent(action, type);
		},
		[sendEvent]
	);

	return {
		create: createTracker(Action.Create),
		replace: createTracker(Action.Replace),
		remove: createTracker(Action.Remove),
		quick: () => sendEvent(Action.Create, 'quick-like'),
	};
};
